import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useUpdateAiTemplateSection } from 'Shared/utils/hooks/use-update-ai-template-section';
import { useUpdateAiReport } from 'Shared/utils/hooks/use-update-ai-report';
import { useUpdateAiSetting } from 'Shared/utils/hooks/use-update-ai-setting';

// Types
import { UpdateAiTemplateSectionType } from 'Shared/types/UpdateAiTemplate.type';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Styles
import './PromptSection.scss';

type Props = {
  section: UpdateAiTemplateSectionType;
  number: number;
  handleRegerateSection: Function;
};

const PromptSection = ({ section, number, handleRegerateSection }: Props) => {
  const { slug } = useParams();
  const {
    handleUpdateTemplateWithDebounce,
    handleRemoveTemplateSection,
    handleUpdateTemplate,
  } = useUpdateAiTemplateSection({
    updateAiSettingSlug: slug,
    section,
  });

  const [isShowingImprovements, toggleIsShowingImprovements] = useToggle(false);
  const { report } = useUpdateAiSetting({ slug });
  const { currentlyGeneratingSlugs } = useUpdateAiReport({
    slug: report.slug,
  });

  const hasGeneratedSectionReport = [
    'ai_generation_completed',
    'ai_generation_cancelled',
    'ai_generation_failed',
  ].includes(report.body.find(b => b.templateSectionSlug === section.slug).state);
  const isRegenerating = currentlyGeneratingSlugs.includes(section.slug);

  const [isTitleInputActive, toggleIsTitleInputActive] = useToggle(false);
  const [title, setTitle] = useState('');
  const [prompt, setPropmt] = useState('');
  const [improvement, setImprovement] = useState('');

  const handleUpdate = updated => {
    handleUpdateTemplateWithDebounce({ data: updated });
  };

  const handleUpdateTitle = (title: string) => {
    setTitle(title);
    handleUpdate({ title });
  };

  const handleUpdatePrompt = (description: string) => {
    setPropmt(description);
    handleUpdate({ description });
  };

  // const handleUpdateImprovement = (improvement: string) => {
  //   setImprovement(improvement);
  //   handleUpdateTemplate({ data: { improvement } });
  // };

  const handleRemoveSectionClick = () => {
    handleRemoveTemplateSection();
  };

  const handleRegenerateSectionClick = () => {
    if (improvement) {
      handleUpdateTemplate({ data: { improvements: [...(section?.improvements || []), improvement] } }).then(() => {
        setImprovement('');
        handleRegerateSection({ templateSectionSlugs: [section.slug] });
      });
    } else {
      handleRegerateSection({ templateSectionSlugs: [section.slug] });
    }
  };

  const handleDeleteLastImprovementClick = () => {
    if (section?.improvements) {
      handleUpdateTemplate({
        data: {
          improvements: section.improvements.filter((improvement, index) => index < section.improvements.length - 1),
        },
      });
    }
  };

  // const calculateHeight = () => {
  //   // basic offset for each item
  //   let height = 44;
  //   let i = 0;
  //   while (i < number) {
  //     height += sections[i]?.height ? sections[i].height : 270;
  //     i++;
  //   }

  //   return height;
  // };
  // const hasHeight = sections.filter(s => !!s.height).length === sections.length;
  // // const hasHeight = !!sections.find(s => s?.height);
  // const styles: CSSProperties = {
  //   position: 'absolute',
  //   top: `${calculateHeight()}px`,
  //   width: '100%',
  //   paddingRight: '1rem',
  // };

  useEffect(() => {
    if (!isTitleInputActive) setTitle(section.title);
  }, [section, isTitleInputActive]);

  useEffect(() => {
    setPropmt(section.description);
  }, []);

  // useEffect(() => {
  //   if (isRegenerating && !currentlyGeneratingSlugs.includes(section.slug)) {
  //     toggleIsRegenerating();
  //   }
  // }, [isRegenerating, currentlyGeneratingSlugs]);

  const baseClassName = 'PromptSection';

  return (
    // <div style={hasHeight ? styles : {}}>
    <WpContainer className={baseClassName}>
      <WpContainerBody padding="05">
        <div className={`${baseClassName}__header`}>
          <p className={`${baseClassName}__title`}>Section {number}</p>
          <WpButtonGroup spacing="sm">
            <WpButton size="sm" variant="tertiary" onClick={handleRemoveSectionClick} disabled={isRegenerating}>
              <WpIcon name="trash" />
            </WpButton>
          </WpButtonGroup>
        </div>
        <div className={`${baseClassName}__title-input`}>
          <WpTextField
            label="Section Header"
            id="title"
            placeholder="Add a title"
            onChange={handleUpdateTitle}
            disabled={isRegenerating}
            value={title}
            max={50}
            onFocus={toggleIsTitleInputActive}
            onBlur={toggleIsTitleInputActive}
          />
        </div>
        <WpTextField
          label="AI Prompt"
          id="prompt"
          placeholder="Give us a prompt"
          multiline={5}
          onChange={handleUpdatePrompt}
          disabled={isRegenerating}
          value={prompt}
          labelIcon="info-circle"
          labelTooltipText="This prompt tells AI what you want in this section"
        />
        {hasGeneratedSectionReport && !isShowingImprovements && (
          <div className={`${baseClassName}__improvement-btn`}>
            <WpButton variant="tertiary" onClick={toggleIsShowingImprovements} size="sm" disabled={isRegenerating}>
              <WpIcon name="plus-circle" />
              Refine Output
            </WpButton>
          </div>
        )}
        {isShowingImprovements && (
          <div className={`${baseClassName}__improvement-input`}>
            <WpTextField
              label="Refine Output"
              id="refine_output"
              placeholder="Examples: Replace the date closed with date opened, make it a paragraph, make it funny"
              multiline={2}
              onChange={setImprovement}
              value={improvement}
              disabled={isRegenerating}
            />
            {!!section?.improvements?.length && (
              <div className={`${baseClassName}__improvement__count`}>
                <small title={section.improvements.join('\n')}>
                  {section.improvements.length} prior refinement{section.improvements.length > 1 ? 's' : ''}
                </small>
                <div className={`${baseClassName}__improvement__count__btn`}>
                  <WpButton variant="tertiary" onClick={handleDeleteLastImprovementClick} size="xs">
                    <WpIcon name="times" />
                  </WpButton>
                </div>
              </div>
            )}
          </div>
        )}
        {hasGeneratedSectionReport && (
          <WpButton size="sm" block variant="secondary" onClick={handleRegenerateSectionClick} loading={isRegenerating}>
            <WpIcon name="redo" />
            Regenerate
          </WpButton>
        )}
      </WpContainerBody>
    </WpContainer>
    // </div>
  );
};

export default PromptSection;
